import type { Decision, GameState, SeasonState } from "../types";
import { Rng, randomSeed, seedFromDate } from "../engine/rng";
import {
  drawCalendar,
  drawRivals,
  newSeasonState,
  pickCard,
  pickDirector,
  rerollDirectorPack,
  rerollKeeping,
  rerollPack,
  rollPack,
} from "../engine/draft";
import { drawWeather, runNextRace } from "../engine/race";
import { playerWccPos } from "../engine/selectors";
import { MAX_TEAM_NAME_LEN } from "../engine/replay";
import { checkBadges } from "../engine/badges";
import { clearState, freshState, loadState, saveState } from "../state/storage";
import { todayKey } from "./helpers";
import { DailyController } from "./dailyController";
import { confirmModal, openHowToModal, openRenameTeamModal, openSuggestModal } from "./modals";
import { renderMenu } from "./screens/menu";
import { renderBadges } from "./screens/badges";
import { renderNaming } from "./screens/naming";
import { renderDraft } from "./screens/draft";
import { renderSeason } from "./screens/season";
import { renderResult } from "./screens/result";
import type { Screen, ScreenContext, ScreenView } from "./screens/context";
import { getLang, LANG_LABEL, LANGS, setLang, t, type Lang } from "./i18n";

// One-time flag: the how-to overlay has been shown. Sibling of the f1dl_* save
// keys; a fresh value, never renamed (renaming would re-show it for everyone).
const INTRO_KEY = "f1dl_intro_seen";

export class App {
  private state: GameState;
  private rng: Rng;
  private app: HTMLElement;
  private status: HTMLElement;
  // Footer disclaimer (static markup, outside #app); kept in sync with the
  // active language on every render.
  private disclaimer = document.getElementById("disclaimer");
  // Transient view state shared with the season/result screens: which standings
  // tab is open, whether the lineup is expanded, how much of the last race's
  // reveal has already played (so a tab switch / language change skips straight
  // to the final state), and the badges just unlocked (shown as a one-time
  // banner). Passed by reference via ctx.view so screen mutations are seen here.
  private view: ScreenView = {
    revealedRaces: 0,
    seasonTab: "wcc",
    lineupExpanded: false,
    resultTab: "wcc",
    justUnlocked: [],
  };
  // The badges screen is a transient view reached from the menu, not a
  // persisted Phase — it always returns to "menu" so it never needs to
  // survive a reload or interact with draft/season validation.
  private showBadges = false;
  private flashTimer: number | undefined;
  // Owns the Daily Challenge leaderboard lifecycle (submit + panel render) and
  // its session-scoped "already tried today" flag. Reads live state through a
  // getter so it survives the reset that reassigns this.state.
  private daily = new DailyController({
    getState: () => this.state,
    persist: () => this.persist(),
  });
  // The handle handed to the extracted screen modules (ui/screens/*). Built once
  // in the constructor; `state` is a getter so screens see the current object
  // even after a reset reassigns this.state.
  private ctx!: ScreenContext;

  constructor(app: HTMLElement, status: HTMLElement) {
    this.app = app;
    this.status = status;
    this.state = loadState();
    const self = this;
    this.ctx = {
      get state() {
        return self.state;
      },
      view: this.view,
      actions: {
        startDaily: () => this.startDaily(),
        startDraft: () => this.startDraft(),
        beginDraft: (name, chaos, fromMemory) => this.beginDraft(name, chaos, fromMemory),
        goMenu: () => this.goMenu(),
        reset: () => this.reset(),
        showHowTo: () => this.showHowTo(),
        showSuggest: () => this.showSuggest(),
        openRename: () => this.showRenameTeam(),
        openBadges: () => {
          this.showBadges = true;
          this.render();
        },
        closeBadges: () => {
          this.showBadges = false;
          this.render();
        },
        onPick: (idx) => this.onPick(idx),
        onRerollPack: () => this.onRerollPack(),
        onRerollKeeping: (idx, keep) => this.onRerollKeeping(idx, keep),
        onPickDirector: (idx) => this.onPickDirector(idx),
        onRerollDirectors: () => this.onRerollDirectors(),
        startOver: () => this.startOver(),
        onRace: () => this.onRace(),
        onViewResult: () => this.onViewResult(),
        abandonSeason: () => this.abandonSeason(),
      },
      daily: this.daily,
      todaysDaily: () => this.todaysDaily(),
      playerWccPos: (s) => this.playerWccPos(s),
      // In-place re-render (no scroll reset) for tab switches / the lineup toggle.
      rerender: () => this.render(false),
    };
    // Resume an in-flight season's RNG at the exact position it left off, so a
    // reload mid-draft/mid-season continues the identical sequence (and a daily
    // stays identical across players). Fresh or pre-seed saves get a random seed.
    const s = this.state.season;
    const resumable =
      this.state.phase === "draft" || this.state.phase === "season" || this.state.phase === "result";
    if (s && resumable && typeof s.seed === "number") {
      this.rng = new Rng(s.seed);
      this.rng.skip(s.rngDraws ?? 0);
    } else {
      this.rng = new Rng(randomSeed());
    }
  }

  start(): void {
    this.setupLangSwitcher();
    this.openDailyFromLinkIfRequested();
    this.render();
    // First-ever visit: surface the how-to once so new players aren't dropped
    // straight into the draft with no explanation. Re-openable from the menu.
    try {
      if (!localStorage.getItem(INTRO_KEY)) {
        localStorage.setItem(INTRO_KEY, "1");
        this.showHowTo();
      }
    } catch {
      // localStorage unavailable (private mode) — skip the one-time intro.
    }
  }

  // A friend's "?daily" link drops them straight into today's Daily Challenge
  // (or its result, if they've already played). Only hijacks a fresh menu — an
  // in-flight season is never interrupted. The param is stripped so a later
  // reload doesn't re-trigger it.
  private openDailyFromLinkIfRequested(): void {
    try {
      const params = new URLSearchParams(location.search);
      if (!params.has("daily")) return;
      params.delete("daily");
      const qs = params.toString();
      history.replaceState(null, "", location.pathname + (qs ? `?${qs}` : "") + location.hash);
      if (this.state.phase === "menu") this.startDaily();
    } catch {
      // Bad URL / no History API — fall through to the normal menu.
    }
  }

  // Thin wrappers around the extracted modal presenters (ui/modals.ts). Kept as
  // bound instance fields because the menu binds them by reference as handlers.
  private showHowTo = (): void => openHowToModal();
  private showSuggest = (): void => openSuggestModal();

  /** Bind the header language selector (lives in static markup, outside #app). */
  private setupLangSwitcher(): void {
    setLang(getLang()); // sync <html lang> on first paint
    const sel = document.getElementById("langSel") as HTMLSelectElement | null;
    if (!sel) return;
    sel.innerHTML = LANGS.map(
      (l) => `<option value="${l}"${l === getLang() ? " selected" : ""}>${LANG_LABEL[l]}</option>`,
    ).join("");
    sel.addEventListener("change", () => {
      setLang(sel.value as Lang);
      // Re-render in place: a language switch shouldn't yank a player who's
      // mid-season back to the top of the page.
      this.render(false);
    });
  }

  private persist(): void {
    // Keep the persisted draw count in step with the live generator so a reload
    // can restore its exact position. Skipped during "naming": the season RNG
    // isn't seeded from s.seed until beginDraft runs.
    const s = this.state.season;
    if (s && this.state.phase !== "naming") s.rngDraws = this.rng.draws;
    saveState(this.state);
  }

  // ---- transitions ----------------------------------------------------------
  private goMenu = (): void => {
    this.state.phase = "menu";
    this.state.season = null;
    this.persist();
    this.render();
  };

  private startDraft = (): void => {
    const s = newSeasonState(this.state.teamName);
    s.seed = randomSeed();
    this.state.season = s;
    this.state.phase = "naming";
    this.persist();
    this.render();
  };

  // Daily Challenge: the same draft and races for everyone today. The seed is
  // pinned to the UTC date and the modifiers are derived from it (not chosen),
  // so every player's run is directly comparable.
  private startDaily = (): void => {
    // Already played today: the daily is one-and-done — show the finished
    // result instead of letting the player replay the same seed.
    const done = this.todaysDaily();
    if (done) {
      this.state.season = done;
      this.state.phase = "result";
      this.persist();
      this.render();
      return;
    }
    // Forfeited earlier today: one attempt per day covers a failed run too, so
    // the same seed is not re-offered. The menu pack shows the failed state.
    if (this.state.dailyFailedDay === todayKey()) {
      this.flash(t().dailyFailed);
      return;
    }
    const day = todayKey();
    const seed = seedFromDate(day);
    const s = newSeasonState(this.state.teamName);
    s.seed = seed;
    s.daily = true;
    s.day = day;
    s.chaos = (seed & 1) === 1; // ~half of days are Chaos days
    s.fromMemory = false; // keep Daily approachable
    this.state.season = s;
    this.state.phase = "naming";
    this.persist();
    this.render();
  };

  private beginDraft = (name: string, chaos: boolean, fromMemory: boolean): void => {
    const s = this.state.season;
    if (!s) return;
    this.rng = new Rng(s.seed);
    s.teamName = name.trim().slice(0, MAX_TEAM_NAME_LEN) || "My Team";
    // Remember the name across runs (the naming screen is also an editing point).
    this.state.teamName = s.teamName;
    s.chaos = chaos;
    s.fromMemory = fromMemory;
    // Draw the calendar up front so the director phase can show the track mix —
    // a street-heavy calendar is what makes the qualifying specialist worth
    // taking over a race strategist. The rival grid is drawn here too so both
    // the calendar AND the opponents are fixed by the seed, before any reroll
    // can shift them — everyone on a daily faces the same world.
    drawCalendar(this.rng, s);
    drawRivals(this.rng, s);
    drawWeather(this.rng, s);
    rollPack(this.rng, s);
    s.rollNo = 1;
    this.state.phase = "draft";
    this.persist();
    this.render();
  };

  // Record one draft action for a Daily run (no-op for free seasons), in the
  // exact order the live RNG consumed it, so engine/replay.ts can reconstruct
  // the run server-side and verify the leaderboard score.
  private logDecision(s: SeasonState, d: Decision): void {
    if (!s.daily) return;
    (s.decisionLog ??= []).push(d);
  }

  private onPick = (idx: number): void => {
    const s = this.state.season;
    if (!s) return;
    const out = pickCard(this.rng, s, idx);
    if (!out.ok) {
      this.flash(out.reason);
      return;
    }
    this.logDecision(s, { t: "pick", i: idx });
    if (out.finalized) {
      this.state.phase = "season";
      this.view.revealedRaces = 0;
    }
    this.persist();
    this.render();
  };

  private onRerollPack = (): void => {
    const s = this.state.season;
    if (!s) return;
    const before = s.rerollLeft;
    rerollPack(this.rng, s);
    // Only log a reroll that actually fired (rerollLeft dropped) — a no-op when
    // the budget is spent would also no-op on replay, but skipping it keeps the
    // log clean.
    if (s.rerollLeft !== before) this.logDecision(s, { t: "reroll" });
    this.persist();
    this.render();
  };

  private onRerollKeeping = (idx: number, keep: "team" | "country"): void => {
    const s = this.state.season;
    if (!s) return;
    const before = s.rerollLeft;
    rerollKeeping(this.rng, s, idx, keep);
    if (s.rerollLeft !== before) this.logDecision(s, { t: "rerollKeep", i: idx, keep });
    this.persist();
    this.render();
  };

  private onPickDirector = (idx: number): void => {
    const s = this.state.season;
    if (!s) return;
    const out = pickDirector(this.rng, s, idx);
    if (!out.ok) {
      this.flash(out.reason);
      return;
    }
    this.logDecision(s, { t: "pickDir", i: idx });
    if (out.finalized) {
      this.state.phase = "season";
      this.view.revealedRaces = 0;
    }
    this.persist();
    this.render();
  };

  private onRerollDirectors = (): void => {
    const s = this.state.season;
    if (!s) return;
    const before = s.rerollLeft;
    rerollDirectorPack(this.rng, s);
    if (s.rerollLeft !== before) this.logDecision(s, { t: "rerollDir" });
    this.persist();
    this.render();
  };

  private onRace = (): void => {
    const s = this.state.season;
    if (!s) return;
    runNextRace(s);
    // Stay on the season screen even once standings are final, so the last
    // race's bottom-to-top reveal and podium play out before the player
    // moves on to the championship screen via "View championship".
    if (s.standings) {
      this.recordHistory(s);
      if (s.daily) {
        this.daily.recordResult(s);
        this.daily.ensureSubmitted(s);
      }
    }
    const newly = checkBadges(this.state.unlockedBadges, s);
    if (newly.length) this.state.unlockedBadges = [...this.state.unlockedBadges, ...newly];
    // Surface the unlock as a banner on the next render; it stays until the
    // player acts again (runs the next race or views the championship).
    this.view.justUnlocked = newly;
    this.persist();
    this.render();
  };

  private onViewResult = (): void => {
    this.state.phase = "result";
    this.persist();
    this.render();
  };

  private recordHistory(s: SeasonState): void {
    const st = s.standings;
    if (!st) return;
    const champ = st.constructors[0]!;
    const wdc = st.drivers[0]!;
    this.state.history.unshift({
      wcc: champ.name,
      wdc: `${wdc.driverName}`,
      playerWonWcc: champ.isPlayer,
      date: Date.now(),
    });
    this.state.history = this.state.history.slice(0, 8);
  }

  /** The completed daily season if one was finished today, else null. */
  private todaysDaily(): SeasonState | null {
    const ds = this.state.dailySeason;
    return ds && ds.day === todayKey() && ds.standings ? ds : null;
  }

  /** The player's final Constructors' position in a finished season (1-based).
   *  Falls back to last place if the player is somehow absent from the grid. */
  private playerWccPos(s: SeasonState): number {
    const st = s.standings;
    if (!st) return 0;
    return playerWccPos(st) || st.constructors.length;
  }

  private reset = (): void => {
    confirmModal(t().resetConfirm, () => {
      clearState();
      this.state = freshState();
      this.render();
    });
  };

  // Discard the in-progress draft and return to the menu, so the player can
  // restart from scratch if they don't like the lineup they're building. On a
  // Daily run there is no restart: the seed is one-and-done, so leaving forfeits
  // today's challenge (recorded as failed) instead of reshuffling for a retry.
  private startOver = (): void => {
    const s = this.state.season;
    if (s?.daily) {
      confirmModal(t().dailyForfeitConfirm, () => this.failDaily(s));
      return;
    }
    confirmModal(t().startOverConfirm, this.goMenu);
  };

  // Leave an in-progress season from the race screen. Free seasons just drop to
  // the menu; a Daily run forfeits (see failDaily) so it can't be replayed today.
  private abandonSeason = (): void => {
    const s = this.state.season;
    if (s?.daily) {
      confirmModal(t().dailyForfeitConfirm, () => this.failDaily(s));
      return;
    }
    this.goMenu();
  };

  // Mark today's Daily Challenge as failed and return to the menu. The failed day
  // is remembered (dailyFailedDay) so startDaily() won't re-offer the same seed —
  // one attempt per day, whether finished or forfeited. No score is submitted.
  private failDaily = (s: SeasonState): void => {
    this.state.dailyFailedDay = s.day ?? todayKey();
    this.state.season = null;
    this.state.phase = "menu";
    this.persist();
    this.render();
  };

  private flash(msg: string, durationMs = 1800): void {
    this.status.textContent = msg;
    // An earlier flash's own revert timer would otherwise fire later and
    // clobber this one early — cancel it so only the latest flash governs.
    if (this.flashTimer !== undefined) window.clearTimeout(this.flashTimer);
    this.flashTimer = window.setTimeout(() => {
      this.status.textContent = t().status;
      this.flashTimer = undefined;
    }, durationMs);
  }

  // ---- render router --------------------------------------------------------
  // resetScroll defaults to true: a full render means a new screen (phase change,
  // a fresh draft roll, the badges view), so the viewport jumps back to the top —
  // otherwise picking a card low on the page leaves you scrolled past the header
  // and slot strip on the next roll. In-place updates that should preserve scroll
  // (tab switches, the lineup toggle) call renderSeason()/renderResult() directly
  // and never reach here; the language switch passes resetScroll=false on purpose.
  private render(resetScroll = true): void {
    if (resetScroll) window.scrollTo(0, 0);
    this.status.textContent = t().status;
    if (this.disclaimer) this.disclaimer.textContent = t().disclaimer;
    switch (this.state.phase) {
      case "menu":
        return this.mount(this.showBadges ? renderBadges(this.ctx) : renderMenu(this.ctx));
      case "naming":
        return this.mount(renderNaming(this.ctx));
      case "draft":
        return this.mount(renderDraft(this.ctx));
      case "season":
        return this.mount(renderSeason(this.ctx));
      case "result":
        return this.mount(renderResult(this.ctx));
    }
  }

  // Inject an extracted screen's markup, then run its post-mount binding step.
  private mount(screen: Screen): void {
    this.app.innerHTML = screen.html;
    screen.bind?.();
  }

  // Edit the persisted team name from the menu, without starting a season. The
  // name is remembered across runs (GameState.teamName) and pre-fills the draft.
  private showRenameTeam = (): void =>
    openRenameTeamModal(this.state.teamName, (name) => {
      this.state.teamName = name;
      this.persist();
      this.render(); // refresh the menu's displayed name
    });
}
